<script setup lang="ts">
import { computed } from 'vue';
import type { DosageForm } from '@/api/medications';
import { useCompanyClock } from '@/composables/useCompanyClock';
import {
  addDays, courseDoses, humanMoment, nextDose, periodEnd, periodStart, type PeriodChoice,
} from '@/utils/medications';

// Third step: when the order starts and how long it lasts, read back as the
// doses it gives ("10 dosis · última mié 21-10 08:00"). A course of single
// doses has no length: they are given at the start.
const props = defineProps<{
  dosages: DosageForm[];
  isChange: boolean;
  startError: string;
  durationError: string;
}>();
const period = defineModel<PeriodChoice>('period', { required: true });
const { now } = useCompanyClock();

const STARTS = [
  { value: 'now', title: 'Ahora' },
  { value: 'date', title: 'Otra fecha' },
];

const DURATIONS = [
  { value: 'open', title: 'Sin término' },
  { value: 'days', title: 'Por días' },
  { value: 'until', title: 'Hasta una fecha' },
];

const FAR_AHEAD_DAYS = 30;

const start = computed(() => periodStart(period.value));
const end = computed(() => periodEnd(period.value, now()));
const startsFarAhead = computed(() => !!start.value && start.value > addDays(now(), FAR_AHEAD_DAYS));
const onlySingleDoses = computed(() => props.dosages.every((dosage) => dosage.kind === 'single'));

const firstDose = computed(() => {
  const dose = nextDose(props.dosages, start.value && start.value > now() ? start.value : now());

  return dose && (!end.value || dose < end.value) ? `Primera dosis ${humanMoment(dose, now())}` : '';
});

const course = computed(() => {
  const doses = end.value && courseDoses(props.dosages, start.value ?? now(), end.value);

  return doses ? `${doses.count} dosis · última ${humanMoment(doses.last, now())}` : '';
});

const summary = computed(() => [firstDose.value, course.value].filter(Boolean).join(' · '));
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex flex-wrap align-start ga-3">
      <v-select
        v-model="period.startMode"
        :items="STARTS"
        :label="isChange ? 'Rige desde' : 'Desde'"
        variant="outlined"
        density="compact"
        hide-details
        class="mode-field"
      />
      <v-text-field
        v-if="period.startMode === 'date'"
        v-model="period.startsAt"
        type="datetime-local"
        label="Fecha y hora de inicio"
        variant="outlined"
        density="compact"
        class="date-field"
        :error-messages="startError"
        :hide-details="!startError"
      />
    </div>
    <div
      v-if="startsFarAhead"
      class="text-body-2 text-warning"
    >
      Empieza en más de {{ FAR_AHEAD_DAYS }} días. Revisa la fecha.
    </div>
    <div
      v-if="isChange"
      class="text-body-2 text-medium-emphasis"
    >
      La indicación actual sigue vigente hasta entonces.
    </div>

    <div
      v-if="!onlySingleDoses"
      class="d-flex flex-wrap align-start ga-3"
    >
      <v-select
        v-model="period.durationMode"
        :items="DURATIONS"
        label="Durante"
        variant="outlined"
        density="compact"
        hide-details
        class="mode-field"
      />
      <v-text-field
        v-if="period.durationMode === 'days'"
        v-model.number="period.durationDays"
        type="number"
        min="1"
        label="Días"
        suffix="días"
        variant="outlined"
        density="compact"
        class="days-field"
        :error-messages="durationError"
        :hide-details="!durationError"
      />
      <v-text-field
        v-else-if="period.durationMode === 'until'"
        v-model="period.endsAt"
        type="datetime-local"
        label="Fecha y hora de término"
        variant="outlined"
        density="compact"
        class="date-field"
        :error-messages="durationError"
        :hide-details="!durationError"
      />
      <span
        v-if="period.durationMode === 'days' && end"
        class="until-text text-body-2 text-medium-emphasis"
      >hasta {{ humanMoment(end, now()) }}</span>
    </div>

    <div
      v-if="summary"
      class="text-body-2 text-medium-emphasis"
    >
      {{ summary }}
    </div>
  </div>
</template>
<style scoped>
.mode-field {
  flex: 0 0 200px;
}

.days-field {
  flex: 0 0 120px;
}

.date-field {
  flex: 0 0 240px;
}

.until-text {
  line-height: 40px;
}
</style>
